import React, { useState } from "react";
import {
  Box,
  Button,
  Col,
  Dialog,
  Flex,
  Icon,
  InputField,
  Row,
  SelectField,
  Typography,
} from "../../../../ppui";
import { AllowanceBox } from "../../../../pages/style";
import { TbAlertTriangle } from "react-icons/tb";
import useHolidayService from "./holiday-service";
import {
  getDateProperFormatForInputFn,
  getDateProperFormatForInputReverseFn,
  getDay,
} from "../../../../utils/common";
import {
  HOLIDAY_INITIAL_STATE,
  HOLIDAY_TYPE,
} from "./holiday-management-constant";

const AdminHolidayManagement = ({ openSnackbar }) => {
  const {
    holidayListinfo,
    holiday,
    errors,
    handleChange,
    handleBlurFn,
    maxFormattedDate,
    minFormattedDate,
    setHoliday,
    handleSubmit,
    setErrors,
    handleUpdate,
    holidayLists,
    pagination,
  } = useHolidayService({ openSnackbar });

  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editHolidayId, setEditHolidayId] = useState(null);

  const handleClickOpen = () => {
    setHoliday(HOLIDAY_INITIAL_STATE);
    setOpen(true);
    setErrors({});
    setEditHolidayId(null);
    setIsEditMode(false);
  };
  const handleEditModalOpen = (data) => {
    setHoliday({
      holidayName: data.holiday_name,
      holidayDate: getDateProperFormatForInputReverseFn(data.holiday_date),
      holidayType: data.holiday_type,
    });
    setOpen(true);
    setEditHolidayId(data?.holiday_id);
    setErrors({});
    setIsEditMode(true);
  };

  const handleClose = () => {
    setOpen(false);
    setHoliday(HOLIDAY_INITIAL_STATE);
    setErrors({});
  };

  const handleSubmitFn = async () => {
    let success = false
    if (editHolidayId) {
      success = await handleUpdate(editHolidayId);
    } else {
      success = await handleSubmit();
    }
    if (!success) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handlePagination = (direction) => {
    const nextPage =
      direction === "next" ? pagination.page + 1 : pagination.page - 1;
    if (nextPage > 0 && nextPage <= pagination.totalPages) {
      holidayLists(nextPage, pagination.pageSize);
    }
  };

  return (
    <>
      {/* Header */}
      <Flex justifyContent="space-between" mb={18} gap={10}>
        <Typography variant="h5" mb={0}>
          Holiday Management
        </Typography>
        <Button onClick={handleClickOpen} variant="outlined" size="sm">
          Create Holiday
        </Button>
      </Flex>

      {holidayListinfo?.length === 0 ? (
        <AllowanceBox mb={20}>
          <Icon color={"danger"} size={"lg"}>
            <TbAlertTriangle />
          </Icon>
          <Typography
            variant="body1"
            display="flex"
            gap={10}
            alignItems="center"
            mb={0}
            p={15}
          >
            No predefined holiday available.
          </Typography>
        </AllowanceBox>
      ) : (
        <>
          {holidayListinfo?.map((holiday, index) => (
            <AllowanceBox key={index} mb={20}>
              <Row alignItems="center" gap={{ md: 20, lg: 30, }} >
                
                <Col xs={12}>
                  <Flex alignItems="center" gap={{ md: 20, lg: 40, xxl: 60 }}>
                    <Box>
                      <Box className="label">Name</Box>
                      <Box
                        onClick={() => handleEditModalOpen(holiday)}
                        className="value"
                      >
                        {holiday.holiday_name}
                      </Box>
                    </Box>
                    <Box>
                      <Box className="label">Date</Box>
                      <Box className="value">
                        {getDateProperFormatForInputFn(holiday.holiday_date)}
                      </Box>
                    </Box>
                    <Box>
                      <Box className="label">Day</Box>
                      <Box className="value">
                        {getDay(holiday.holiday_date)}
                      </Box>
                    </Box>
                    <Box>
                      <Box className="label">Type</Box>
                      <Box className="value">
                        {holiday?.holiday_type}
                      </Box>
                    </Box>
                  </Flex>
                </Col>
              </Row>
            </AllowanceBox>
          ))}
          <Box mt={20}>
            <Flex alignItem="center" justifyContent="center" gap={10}>
              <Button
                color="default"
                colorvariant="main"
                variant="outlined"
                size="sm"
                disabled={pagination.page === 1}
                onClick={() => handlePagination("previous")}
              >
                Previous
              </Button>
              <Typography
                variant="body2"
                mb="0"
                color="dark"
                colorvariant="light"
              >
                Page {pagination.page} of {pagination.totalPages}
              </Typography>
              <Button
                color="default"
                colorvariant="main"
                variant="outlined"
                size="sm"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => handlePagination("next")}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </>
      )}

      <Dialog
        title={isEditMode ? "Edit Holiday" : "Create Holiday"}
        closeButton
        fullWidth
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmitFn}
        separator
        bodyScroll={true}
      // footer={
      //     <Box mr="auto">
      //         <Checkbox size="sm" color="primary" label="Add another holiday" />
      //     </Box>
      // }
      >
        <Row rowGap={20}>
          <Col sm={12}>
            <InputField
              required
              fullWidth
              label="Holiday Name"
              type="text"
              placeholder="Type Here"
              value={holiday.holidayName}
              valueSinc={!!holiday.holidayName}
              onChange={(e) => handleChange(e.target.value, "holidayName")}
              helperText={errors.holidayName}
              error={errors.holidayName}
              onBlur={(e) => handleBlurFn(e, "holidayName")}
            />
          </Col>

          <Col sm={12}>
            <InputField
              required
              fullWidth
              label="Date"
              type="date"
              valueSinc
              max={maxFormattedDate}
              min={minFormattedDate}
              value={holiday.holidayDate}
              onChange={(e) => handleChange(e.target.value, "holidayDate")}
              helperText={errors.holidayDate}
              error={errors.holidayDate}
              onKeyDown={(e) => e.preventDefault()}
              onBlur={(e) => handleBlurFn(e, "holidayDate")}
            />
          </Col>
          <Col sm={12}>
            <SelectField
              fullWidth
              label="Holiday Type"
              options={HOLIDAY_TYPE}
              placeholder="Select Here..."
              // valueSinc={!!holiday.holidayType}
              defaultValue={holiday.holidayType}
              onChange={(val) => handleChange(val, "holidayType")}
              error={errors.holidayType}
              helperText={errors.holidayType}
              onBlur={(e) => handleBlurFn(e, "holidayType")}
            />
          </Col>
        </Row>
      </Dialog>
    </>
  );
};

export default AdminHolidayManagement;
